import type {
  AccessGrantResponse,
  IdentityMembershipSummary,
  IdentityUserSummary,
  ImpersonationSummary,
} from '@ghalishor/contracts';

export const LAST_SELECTED_BUSINESS_STORAGE_KEY = 'ghalishor.lastBusinessId' as const;

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface IdentitySessionSnapshot {
  readonly authenticated: boolean;
  readonly accessExpiresAt: string | null;
  readonly user: IdentityUserSummary | null;
  readonly memberships: readonly IdentityMembershipSummary[];
  readonly selectedBusinessId: string | null;
  readonly impersonation: ImpersonationSummary | null;
}

const cloneMembership = (
  membership: IdentityMembershipSummary,
): IdentityMembershipSummary => ({
  ...membership,
  roleKeys: [...membership.roleKeys],
});

export class MemoryIdentitySessionStore {
  private accessToken: string | null = null;
  private accessExpiresAt: string | null = null;
  private user: IdentityUserSummary | null = null;
  private memberships: readonly IdentityMembershipSummary[] = [];
  private impersonation: ImpersonationSummary | null = null;

  constructor(private readonly storage: StorageLike | null = null) {}

  getAccessToken(): string | null {
    return this.accessToken;
  }

  setAccessGrant(grant: AccessGrantResponse): void {
    this.accessToken = grant.accessToken;
    this.accessExpiresAt = grant.accessExpiresAt;
  }

  setIdentity(
    user: IdentityUserSummary,
    memberships: readonly IdentityMembershipSummary[],
    impersonation: ImpersonationSummary | null = null,
  ): void {
    this.user = { ...user };
    this.memberships = memberships.map(cloneMembership);
    this.impersonation = impersonation === null ? null : { ...impersonation };
  }

  replaceMemberships(memberships: readonly IdentityMembershipSummary[]): void {
    this.memberships = memberships.map(cloneMembership);
  }

  clearAuthentication(options: { readonly preserveBusinessSelection?: boolean } = {}): void {
    this.accessToken = null;
    this.accessExpiresAt = null;
    this.user = null;
    this.memberships = [];
    this.impersonation = null;

    if (options.preserveBusinessSelection === false) {
      this.clearSelectedBusinessId();
    }
  }

  setSelectedBusinessId(businessId: string): void {
    const normalized = businessId.trim();
    if (normalized.length === 0) {
      this.clearSelectedBusinessId();
      return;
    }

    this.storage?.setItem(LAST_SELECTED_BUSINESS_STORAGE_KEY, normalized);
  }

  getSelectedBusinessId(): string | null {
    const value = this.storage?.getItem(LAST_SELECTED_BUSINESS_STORAGE_KEY)?.trim() ?? '';
    return value.length > 0 ? value : null;
  }

  clearSelectedBusinessId(): void {
    this.storage?.removeItem(LAST_SELECTED_BUSINESS_STORAGE_KEY);
  }

  hasActiveMembership(businessId: string): boolean {
    return this.memberships.some(
      (membership) =>
        membership.businessId === businessId && membership.status === 'active',
    );
  }

  snapshot(): IdentitySessionSnapshot {
    return Object.freeze({
      authenticated: this.accessToken !== null,
      accessExpiresAt: this.accessExpiresAt,
      user: this.user === null ? null : Object.freeze({ ...this.user }),
      memberships: Object.freeze(this.memberships.map(cloneMembership)),
      selectedBusinessId: this.getSelectedBusinessId(),
      impersonation: this.impersonation === null ? null : Object.freeze({ ...this.impersonation }),
    });
  }
}
